import axios from 'axios';
import API_BASE_URL, { API_BASE_URL_WITHOUT_API } from '../config/api';

/**
 * Customer-facing API client for the website. Mirrors what the mobile app
 * calls: public search/detail endpoints plus the customer auth, wishlist and
 * profile routes. Its own axios instance, so the admin panel's token never
 * leaks into customer calls (and the reverse).
 */

export const TOKEN_KEY = 'weotp:customer:token';
export const CUSTOMER_KEY = 'weotp:customer';

export const readToken = () => {
  try {
    return window.localStorage.getItem(TOKEN_KEY) || '';
  } catch (error) {
    return '';
  }
};

const client = axios.create({ baseURL: API_BASE_URL, timeout: 30000 });

client.interceptors.request.use((config) => {
  const token = readToken();
  if (token) {
    config.headers = { ...(config.headers || {}), Authorization: `Bearer ${token}` };
  }
  return config;
});

/** Absolute URL for an uploaded image path like /uploads/x.jpg */
export const imageUrl = (path) => {
  if (!path) return '';
  return /^https?:\/\//.test(path) ? path : `${API_BASE_URL_WITHOUT_API}${path}`;
};

/** Error message from an axios failure, in words a customer can read */
export const errorMessage = (error, fallback = 'Something went wrong. Please try again.') =>
  error?.response?.data?.message || (error?.code === 'ECONNABORTED' ? 'The request timed out.' : fallback);

// Removes blank values so the API sees only what was really chosen
const compact = (body) =>
  Object.fromEntries(
    Object.entries(body || {}).filter(([, value]) => value !== undefined && value !== null && value !== '')
  );

const data = (promise) => promise.then((response) => response.data);

/* ------------------------------- listings -------------------------------- */

export const searchHotels = (body) => data(client.post('/public/hotels', compact(body)));
export const getHotel = (id, body) => data(client.post(`/public/hotels/${id}`, compact(body)));
export const searchSpas = (body) => data(client.post('/public/spas', compact(body)));
export const getSpa = (id, body) => data(client.post(`/public/spas/${id}`, compact(body)));
export const searchTours = (body) => data(client.post('/public/tours', compact(body)));
export const getTour = (id, body) => data(client.post(`/public/tours/${id}`, compact(body)));
export const searchPackages = (body) => data(client.post('/public/travel-packages', compact(body)));
export const getPackage = (id, body) => data(client.post(`/public/travel-packages/${id}`, compact(body)));

export const featuredHotels = () => data(client.get('/public/featured-hotels'));
export const featuredSpas = () => data(client.get('/public/featured-spas'));
export const featuredTours = () => data(client.get('/public/featured-tours'));
export const featuredPackages = () => data(client.get('/public/featured-travel-packages'));

export const calculatePrice = (body) => data(client.post('/public/calculate-price', compact(body)));

/* -------------------------------- places --------------------------------- */

export const getCities = () => data(client.get('/public/cities'));
export const getSuggestedDestinations = () => data(client.get('/public/suggested-destinations'));
export const placeAutocomplete = (input) => data(client.get('/public/place/autocomplete', { params: { input } }));
export const placeDetails = (placeId) => data(client.get('/public/place/details', { params: { placeId } }));

/* ------------------------------- customer -------------------------------- */

export const sendOtp = (phone) => data(client.post('/customers/auth/send-otp', { phone }));
export const verifyOtp = (phone, otp) => data(client.post('/customers/auth/verify-otp', { phone, otp }));
export const updateProfile = (formData) =>
  data(client.put('/customers/profile', formData, { headers: { 'Content-Type': 'multipart/form-data' } }));
export const deleteAccount = () => data(client.delete('/customers/account'));

// kind: hotels | spas | tours | travel-packages
export const getWishlist = (kind) => data(client.get(`/customers/wishlist/${kind}`));
export const addToWishlist = (kind, id) => data(client.post(`/customers/wishlist/${kind}/${id}`));
export const removeFromWishlist = (kind, id) => data(client.delete(`/customers/wishlist/${kind}/${id}`));

/* --------------------------------- misc ---------------------------------- */

export const getLegalPage = (slug) => data(client.get(`/public/legal-pages/${slug}`));
export const getConfig = () => data(client.get('/public/config'));

export default client;
